import React from 'react';
import { motion } from 'framer-motion';
import { Clock, TrendingDown, Star } from 'lucide-react';
import { businessBenefits } from '../data/mock';

const iconMap = {
  Clock: Clock,
  TrendingDown: TrendingDown,
  Star: Star,
};

const ForBusinessesSection = () => {
  return (
    <section
      id="for-businesses"
      className="section-padding"
      style={{ background: 'var(--bg-section)' }}
    >
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
              style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
            >
              For Businesses
            </span>
            <h2 className="heading-2 mb-6">Never understaffed again</h2>
            <p className="body-large mb-8">
              Whether it's a last-minute no-show or a busy weekend, Crew Up connects you with
              verified hospitality professionals in minutes.
            </p>

            <div className="space-y-6">
              {businessBenefits.map((benefit, index) => {
                const Icon = iconMap[benefit.icon];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
                      style={{ background: 'var(--accent-wash)' }}
                    >
                      <Icon size={24} style={{ color: 'var(--accent-text)' }} />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                        {benefit.title}
                      </h4>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {benefit.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Visual Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-black/5">
              {/* Mock Dashboard */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Today's Shifts
                  </h4>
                  <span
                    className="text-xs px-3 py-1 rounded-full"
                    style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
                  >
                    3 Active
                  </span>
                </div>

                {/* Shift Cards */}
                {[
                  { role: 'Server', time: '6PM - 11PM', status: 'Filled', person: 'Sarah M.' },
                  { role: 'Bartender', time: '7PM - 1AM', status: 'Filled', person: 'John K.' },
                  { role: 'Host', time: '5PM - 10PM', status: 'Open', person: null },
                ].map((shift, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-3 border-b last:border-0"
                    style={{ borderColor: 'var(--border-light)' }}
                  >
                    <div>
                      <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                        {shift.role}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {shift.time}
                      </p>
                    </div>
                    <div className="text-right">
                      {shift.status === 'Filled' ? (
                        <>
                          <p className="text-xs font-medium" style={{ color: 'var(--accent-text)' }}>
                            {shift.status}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {shift.person}
                          </p>
                        </>
                      ) : (
                        <span
                          className="text-xs px-2 py-1 rounded-full"
                          style={{ background: '#FEF3C7', color: '#92400E' }}
                        >
                          Finding staff...
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>98%</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Fill Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>&lt;2h</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Avg Fill Time</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>4.9</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Staff Rating</p>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div
              className="absolute -z-10 -top-4 -right-4 w-full h-full rounded-3xl"
              style={{ background: 'var(--accent-wash)' }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ForBusinessesSection;
